import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { Box, Spinner } from '@cloudscape-design/components';
import { exchangeToken } from '../common/api/distributorDashboard';
import { useDistributorAuth } from '../auth/useDistributorAuth';
import { useAuth } from '../auth/useAuth';

export const Route = createFileRoute('/auth/callback')({
  validateSearch: (search: Record<string, unknown>) => ({
    code: (search.code as string) || '',
    state: (search.state as string) || '',
  }),
  component: AuthCallbackPage,
});

function AuthCallbackPage() {
  const { code, state } = Route.useSearch();
  const navigate = useNavigate();
  const { login: distributorLogin } = useDistributorAuth();
  const { login: internalLogin } = useAuth();

  useEffect(() => {
    if (!code || !state) {
      window.location.href = 'https://manage.realtimefeedback.com';
      return;
    }

    let cancelled = false;

    exchangeToken(code, state)
      .then((result) => {
        if (cancelled) return;

        // Internal users (RTF/aiescape) → dashboard
        if (result.user_type === 'internal') {
          internalLogin(
            result.token,
            {
              email: result.distributor?.email ?? 'unknown',
              name: result.distributor
                ? `${result.distributor.first_name} ${result.distributor.last_name ?? ''}`.trim()
                : 'Internal User',
              user_type: 'internal',
            },
            result.business_id,
          );
          navigate({ to: '/dashboard' });
          return;
        }

        // Distributors → distributor dashboard
        if (result.distributor) {
          distributorLogin(result.token, result.distributor);
          navigate({ to: '/distributor' });
          return;
        }

        // External redirect
        if (result.redirect_to) {
          window.location.href = result.redirect_to;
          return;
        }

        // Fallback
        window.location.href = 'https://manage.realtimefeedback.com';
      })
      .catch(() => {
        if (cancelled) return;
        window.location.href = 'https://manage.realtimefeedback.com';
      });

    return () => {
      cancelled = true;
    };
  }, [code, state, distributorLogin, internalLogin, navigate]);

  return (
    <Box padding="xxl" textAlign="center">
      <Spinner size="large" />
      <Box padding="s">Authenticating...</Box>
    </Box>
  );
}
